import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Users, MapPin, ShieldCheck, ArrowRight, Coins, Facebook, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureFunnelFromUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1749451464307-efce2ae4c451?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBzaGFyaW5nJTIwaXRlbXN8ZW58MHx8fHwxNzg0OTkyMjI4fDA&ixlib=rb-4.1.0&q=85";

const perks = [
  { icon: Coins, title: "5 crediti di benvenuto", text: "Più 5 crediti extra se arrivi con l'invito di un amico del gruppo." },
  { icon: MapPin, title: "Oggetti nella tua zona", text: "Ritrova gli annunci del gruppo, ora organizzati e ricercabili." },
  { icon: ShieldCheck, title: "Scambi più sicuri", text: "Profili verificati, recensioni e moderazione degli annunci." },
];

export default function Gruppo() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hasRef, setHasRef] = useState(false);

  useEffect(() => {
    captureFunnelFromUrl();
    if (!localStorage.getItem("dona_utm_source")) localStorage.setItem("dona_utm_source", "facebook_gruppo");
    setHasRef(!!localStorage.getItem("dona_ref"));
  }, []);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 glass border-b border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Gift className="h-5 w-5 text-primary-foreground" /></div>
            <span className="font-head font-extrabold text-xl text-primary">Ti Regalo</span>
          </div>
          <Button onClick={() => navigate(user ? "/app" : "/register")} className="rounded-full" data-testid="gruppo-header-cta">{user ? "Vai alla piattaforma" : "Iscriviti gratis"}</Button>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-14">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#1877F2]/10 border border-[#1877F2]/30 px-4 py-1.5 text-sm font-semibold text-[#1877F2]">
          <Facebook className="h-4 w-4" /> Benvenuto dalla community Facebook
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-center mt-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">
              Il nostro gruppo diventa <span className="text-accent">un'app tutta tua</span>.
            </h1>
            <p className="text-lg text-muted-foreground mt-5 max-w-md">
              Tutto quello che ami del gruppo — regalare e ricevere oggetti gratis — ora con profili, chat, ricerca per zona e crediti che valgono vantaggi reali.
            </p>
            {hasRef && (
              <div className="mt-5 rounded-xl bg-success/10 border border-success/30 px-4 py-3 text-sm flex items-center gap-2" data-testid="gruppo-ref-banner">
                <Heart className="h-4 w-4 text-success" /> Hai un invito attivo: riceverai <b>5 crediti bonus</b> all'iscrizione!
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" onClick={() => navigate("/register")} className="rounded-full h-12 px-7 text-base" data-testid="gruppo-cta-register">
                Crea il tuo profilo gratis <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/login")} className="rounded-full h-12 px-7 text-base" data-testid="gruppo-cta-login">Ho già un account</Button>
            </div>
            <div className="flex items-center gap-2 mt-6 text-sm text-muted-foreground"><Users className="h-4 w-4" /> Unisciti a migliaia di membri già migrati</div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <img src={HERO} alt="Community" className="rounded-[2rem] w-full aspect-square object-cover tactile-shadow" />
          </motion.div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {perks.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="rounded-2xl bg-card border border-border/60 p-6 tactile-shadow">
              <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><p.icon className="h-5 w-5 text-primary" /></div>
              <h3 className="font-head font-bold text-lg mt-4">{p.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{p.text}</p>
            </motion.div>
          ))}
        </div>
        <div className="rounded-[2rem] bg-primary text-primary-foreground p-10 sm:p-14 text-center mt-10">
          <h2 className="font-head font-extrabold text-3xl">Pronto a passare a Ti Regalo?</h2>
          <p className="text-primary-foreground/80 mt-3">Iscriviti in 30 secondi e porta con te la community.</p>
          <Button size="lg" variant="secondary" onClick={() => navigate("/register")} className="rounded-full mt-8 h-12 px-8 text-base" data-testid="gruppo-footer-cta">Inizia ora — è gratis</Button>
        </div>
      </section>
    </div>
  );
}
